import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import CurrentWaypoint from './CurrentWaypoint';
import CustomMarker from './CustomMarker';
import { GoogleMap, Polyline, TrafficLayer } from '@react-google-maps/api';
import {
  normalizeMapLatLng,
  isValidMapPosition,
  readGoogleLatLng,
} from './locationHelper';
import { getDirections, normalizeRoutePreference } from '../../../services/directionsService';
import { useSmoothedVehiclePosition } from '../../../hooks/useSmoothedVehiclePosition';

const ROUTE_PREF_STORAGE_KEY = 'tranware_driver_route_preference';

/**
 * Optional [Cloud map ID](https://developers.google.com/maps/documentation/javascript/map-ids).
 * Short or invalid IDs break the vector map stack (blank map, "Ea"/"HL" minified errors).
 */
const RAW_CLOUD_MAP_ID = String(process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '').trim();
const SAFE_CLOUD_MAP_ID =
  RAW_CLOUD_MAP_ID.length >= 16 && /^[a-zA-Z0-9_-]+$/.test(RAW_CLOUD_MAP_ID)
    ? RAW_CLOUD_MAP_ID
    : '';

const mapOptions = (() => {
  const o = {
    mapTypeId: 'roadmap',
    zoom: 14,
    heading: 0,
    gestureHandling: 'greedy',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false,
    cameraControl: false,
    clickableIcons: false,
    disableDefaultUI: true,
  };
  if (SAFE_CLOUD_MAP_ID) {
    o.mapId = SAFE_CLOUD_MAP_ID;
  }
  return o;
})();

/** Meters between two { lat, lng } points (WGS84). */
function haversineMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Project vehicle onto segment a→b (local equirectangular approx); returns closest point + distance. */
function projectOntoSegmentMeters(v, a, b) {
  const lat0 = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const cos = Math.max(Math.cos(lat0), 1e-8);
  const ax = a.lng * cos;
  const ay = a.lat;
  const bx = b.lng * cos;
  const by = b.lat;
  const vx = v.lng * cos;
  const vy = v.lat;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 < 1e-16 ? 0 : Math.max(0, Math.min(1, ((vx - ax) * dx + (vy - ay) * dy) / len2));
  const py = ay + t * dy;
  const px = (ax + t * dx) / cos;
  const point = { lat: py, lng: px };
  return { point, distM: haversineMeters(v, point) };
}

/**
 * Remaining route only: polyline **starts at the vehicle** then follows the road ahead.
 * Never return the full geometry here — avoids “already driven” segment when GPS drifts from the line.
 */
function trimPolylineToRemainingAhead(path, vehicle, minSegFloor) {
  if (!path || path.length < 2 || !vehicle || !isValidMapPosition(vehicle)) {
    return { points: path || [], advanceSegIdx: 0 };
  }
  let best = { distM: Infinity, segIdx: 0, point: path[0] };
  for (let i = 0; i < path.length - 1; i++) {
    const p = projectOntoSegmentMeters(vehicle, path[i], path[i + 1]);
    if (p.distM < best.distM) {
      best = { distM: p.distM, segIdx: i, point: p.point };
    }
  }
  const floor = Math.max(0, minSegFloor - 3);
  const segIdx = Math.max(floor, best.segIdx);
  const tail = path.slice(segIdx + 1);
  /** First vertex is always the car so nothing draws “behind” the marker. */
  const out = [{ lat: vehicle.lat, lng: vehicle.lng }];
  if (haversineMeters(vehicle, best.point) > 4) {
    out.push(best.point);
  }
  for (let i = 0; i < tail.length; i++) {
    const p = tail[i];
    if (haversineMeters(out[out.length - 1], p) > 1.5) out.push(p);
  }
  if (out.length < 2) {
    out.push(path[path.length - 1]);
  }
  return { points: out.length >= 2 ? out : path, advanceSegIdx: best.segIdx };
}

/** Movement threshold + debounce to limit routing API calls (spec: ~20 m, ~5–10 s). */
const LIVE_ROUTE_MIN_INTERVAL_MS = 8000;
const LIVE_ROUTE_MIN_MOVE_M = 20;

function readRoutePreference(fleetSettings) {
  try {
    const f =
      fleetSettings?.routePreference ??
      fleetSettings?.RoutePreference ??
      fleetSettings?.routeOptimization;
    if (f === 'shortest' || f === 'fastest') return f;
    const ls = localStorage.getItem(ROUTE_PREF_STORAGE_KEY);
    if (ls === 'shortest' || ls === 'fastest') return ls;
  } catch {
    /* ignore */
  }
  return normalizeRoutePreference(process.env.REACT_APP_DEFAULT_ROUTE_PREFERENCE);
}

const WaypointMap = ({
  isPhone,
  googleApiKey = null,
  /** Must match App useLoadScript isLoaded so Map never mounts mid-script (Maps v3 "pH" crash). */
  googleMapsLoaded = false,
  center,
  onMapLoad,
  waypoint,
  allWaypoints = [], // For checking wheelchair trips
  vehicleOrientation = 0,
  vehicleStatus = 'Clear', // New prop for vehicle status
  expectedTime,
  routeDriveSummary = null,
  onDirectionsCalculated,
  isAccurate,
  handleRequestDirections,
  onWaypointAction,
  softMeterState,
  softMeterRouteOverride = null,
  onSetSoftMeterRouteOverride,
  onClearSoftMeterRouteOverride,
  meterRatesReady = false,
  canFlag = false,
  fareConfirmationOpen = false,
  onFlagTripModalClosed,
  onFlagTripModalOpen,
  onFlagTripModalDismiss,
  registerPendingFlagAck,
  fleetSettings,
}) => {
  const [routePreference, setRoutePreference] = useState(() => readRoutePreference(fleetSettings));

  useEffect(() => {
    setRoutePreference(readRoutePreference(fleetSettings));
  }, [fleetSettings]);

  const [polylinePath, setPolylinePath] = useState([]);
  /** Polyline drawn on map: remaining leg only (trimmed from full geometry below). */
  const [remainingPolylinePath, setRemainingPolylinePath] = useState([]);
  const routeProgressSegRef = useRef(0);
  const [request, setRequest] = useState(null);
  const [showDirectionsService, setShowDirectionsService] = useState(false);
  /** Avoid mounting markers before the map projection is ready (prevents native overlay glitches). */
  const [mapProjectionReady, setMapProjectionReady] = useState(false);
  const liveRouteMetaRef = useRef({ lastFetchAt: 0, lastOrigin: null });

  const persistRoutePreference = useCallback((pref) => {
    const v = pref === 'shortest' ? 'shortest' : 'fastest';
    setRoutePreference(v);
    try {
      localStorage.setItem(ROUTE_PREF_STORAGE_KEY, v);
    } catch {
      /* ignore */
    }
    liveRouteMetaRef.current = { lastFetchAt: 0, lastOrigin: null };
  }, []);

  // Stable object identity unless lat/lng actually change (~1m rounding). Rapid GPS object
  // churn with the same coordinates was destabilizing the Maps JS stack (minified "pH" errors).
  const mapCenterKey = useMemo(() => {
    const n = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    if (!n || !isValidMapPosition(n)) return 'fallback';
    return `${n.lat.toFixed(5)},${n.lng.toFixed(5)}`;
  }, [center]);

  const mapCenter = useMemo(() => {
    const n = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    if (n && isValidMapPosition(n)) return { lat: n.lat, lng: n.lng };
    return { lat: 28.05, lng: -82.4483 };
  }, [mapCenterKey]);

  const vehicleDisplayPosition = useSmoothedVehiclePosition(
    isValidMapPosition(mapCenter) ? mapCenter : null
  );
  const vehicleMapPosition =
    vehicleDisplayPosition && isValidMapPosition(vehicleDisplayPosition)
      ? vehicleDisplayPosition
      : mapCenter;

  useEffect(() => {
    liveRouteMetaRef.current = { lastFetchAt: 0, lastOrigin: null };
  }, [routePreference]);

  const osrmUrl = fleetSettings?.osrmUrl || fleetSettings?.osrmBaseUrl || fleetSettings?.OsrmBaseUrl || null;
  const meterRunning = !!softMeterState?.isRunning;

  const waypointMapPosition = useMemo(() => {
    if (!waypoint?.location) return null;
    let p = null;
    if (Array.isArray(waypoint.location) && waypoint.location.length >= 2) {
      p = normalizeMapLatLng([waypoint.location[0], waypoint.location[1]]);
    } else if (typeof waypoint.location === 'object') {
      p = normalizeMapLatLng(waypoint.location);
    }
    return p && isValidMapPosition(p) ? p : null;
  }, [waypoint?.location, waypoint?.jobNumber]);

  /** SoftMeter manual destination only; origin for routing is always live vehicle (`center`). */
  const meterRouteOverride = useMemo(() => {
    if (!meterRunning) return null;
    const destination = normalizeMapLatLng(softMeterRouteOverride?.destination);
    if (!destination || !isValidMapPosition(destination)) return null;
    return { destination };
  }, [meterRunning, softMeterRouteOverride]);

  /** Manual SoftMeter drop override or dispatch dropoff coordinate — vehicle → this point. */
  const liveNavDestination = useMemo(() => {
    const manual = normalizeMapLatLng(softMeterRouteOverride?.destination);
    if (meterRunning && manual && isValidMapPosition(manual)) return manual;
    if (
      waypoint &&
      String(waypoint.type || '').toLowerCase() === 'dropoff' &&
      waypointMapPosition &&
      isValidMapPosition(waypointMapPosition)
    ) {
      return waypointMapPosition;
    }
    return null;
  }, [meterRunning, softMeterRouteOverride, waypoint, waypointMapPosition]);

  const liveNavDestKey = useMemo(() => {
    const d = liveNavDestination;
    if (!d) return '';
    return `${d.lat.toFixed(6)},${d.lng.toFixed(6)}`;
  }, [liveNavDestination]);

  useEffect(() => {
    liveRouteMetaRef.current = { lastFetchAt: 0, lastOrigin: null };
  }, [liveNavDestKey]);

  const activeRouteDestination = useMemo(() => {
    if (meterRouteOverride) return meterRouteOverride.destination;
    return waypointMapPosition;
  }, [meterRouteOverride, waypointMapPosition]);

  const hasManualDropOverride = useMemo(() => {
    const d = normalizeMapLatLng(softMeterRouteOverride?.destination);
    return !!(meterRunning && d && isValidMapPosition(d));
  }, [meterRunning, softMeterRouteOverride]);

  /** Live route: driver GPS → drop; throttled (movement + time) to limit API usage. */
  useEffect(() => {
    if (!liveNavDestination || !isAccurate) {
      liveRouteMetaRef.current = { lastFetchAt: 0, lastOrigin: null };
      return;
    }
    const dest = liveNavDestination;
    const origin = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    if (!origin || !isValidMapPosition(origin) || !dest || !isValidMapPosition(dest)) return;

    const meta = liveRouteMetaRef.current;
    const now = Date.now();
    let moved = Infinity;
    if (meta.lastOrigin && isValidMapPosition(meta.lastOrigin)) {
      moved = haversineMeters(meta.lastOrigin, origin);
    }
    const elapsed = now - meta.lastFetchAt;
    const firstFetch = meta.lastFetchAt === 0;
    if (!firstFetch && elapsed < LIVE_ROUTE_MIN_INTERVAL_MS && moved < LIVE_ROUTE_MIN_MOVE_M) {
      return;
    }

    meta.lastFetchAt = now;
    meta.lastOrigin = { lat: origin.lat, lng: origin.lng };

    setRequest({
      origin,
      destination: dest,
      travelMode: 'DRIVING',
    });
    setShowDirectionsService(true);
  }, [liveNavDestination, isAccurate, mapCenterKey, center]);

  /** New full geometry from API → restart progress along polyline. */
  useEffect(() => {
    routeProgressSegRef.current = 0;
  }, [polylinePath]);

  /** Between full route refetches: shrink polyline to “remaining ahead” as GPS moves (no extra API call). */
  useEffect(() => {
    if (!polylinePath.length) {
      setRemainingPolylinePath([]);
      return;
    }
    /** Match visible vehicle arrow (smoothed) so the line starts where the driver sees the icon. */
    const v =
      vehicleMapPosition && isValidMapPosition(vehicleMapPosition)
        ? vehicleMapPosition
        : readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    if (!v || !isValidMapPosition(v)) {
      setRemainingPolylinePath(polylinePath);
      return;
    }
    const { points, advanceSegIdx } = trimPolylineToRemainingAhead(
      polylinePath,
      v,
      routeProgressSegRef.current
    );
    routeProgressSegRef.current = Math.max(routeProgressSegRef.current, advanceSegIdx);
    setRemainingPolylinePath(points);
  }, [polylinePath, mapCenterKey, center, vehicleMapPosition]);

  // Clear polyline when the active job changes (not on Pickup→Dropoff within the same job).
  useEffect(() => {
    setPolylinePath([]);
    setRemainingPolylinePath([]);
    routeProgressSegRef.current = 0;
    setRequest(null);
    setShowDirectionsService(false);
  }, [waypoint?.jobNumber]);

  // Fetch route polyline: OSRM when configured, else Google DirectionsService (see directionsService.js).
  useEffect(() => {
    if (!showDirectionsService || !request) return;
    let cancelled = false;
    const origin = readGoogleLatLng(request.origin) ?? normalizeMapLatLng(request.origin);
    const destination =
      readGoogleLatLng(request.destination) ?? normalizeMapLatLng(request.destination);
    if (!origin || !destination || !isValidMapPosition(origin) || !isValidMapPosition(destination)) {
      setShowDirectionsService(false);
      return;
    }
    getDirections({
      origin,
      destination,
      osrmUrl,
      trafficAware: true,
      routePreference,
      retryAttempts: 3,
    })
      .then((result) => {
        if (cancelled) return;
        if (result && Array.isArray(result.path) && result.path.length > 0) {
          setPolylinePath(result.path);
          if (typeof result.duration === 'number' && onDirectionsCalculated) {
            onDirectionsCalculated(result.duration, {
              usedTrafficDuration: !!result.usedTrafficDuration,
              durationBaselineSeconds: result.durationBaselineSeconds,
              distanceMeters: typeof result.distance === 'number' ? result.distance : undefined,
              routePreference: result.routePreference || routePreference,
            });
          }
        } else {
          console.warn('WaypointMap: no route path (OSRM/Google); keeping previous polyline if any');
        }
        setShowDirectionsService(false);
      })
      .catch(() => {
        if (cancelled) return;
        console.warn('WaypointMap: directions request failed; keeping previous polyline if any');
        setShowDirectionsService(false);
      });
    return () => { cancelled = true; };
  }, [showDirectionsService, request, osrmUrl, onDirectionsCalculated, routePreference]);

  // Map height is locked. Content below the map scrolls (see overflow-y-auto wrapper) so the map
  // never gets shrunk by a tall trip card / live meter / "rates not received" warning.
  const mapContainerStyle = !isPhone
    ? { width: '100%', height: '50vh', flexShrink: 0 } // desktop / tablet
    : { width: '100%', height: '300px', flexShrink: 0 }; // phone

  const onLoad = useCallback(
    (map) => {
      setMapProjectionReady(false);
      if (onMapLoad) onMapLoad(map);

      // Attach the callbacks to the map object for external access
      map.__reactRootDispatchesMap = {
        onRequestDirections: setRequest,
        onToggleDirectionsService: setShowDirectionsService,
      };

      if (map && window.google?.maps?.event) {
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
          setMapProjectionReady(true);
        });
      } else {
        setMapProjectionReady(true);
      }
    },
    [onMapLoad]
  );

  const onUnmount = useCallback(() => {
    setMapProjectionReady(false);
    setPolylinePath([]);
    setRemainingPolylinePath([]);
    routeProgressSegRef.current = 0;
    setRequest(null);
    setShowDirectionsService(false);
  }, []);

  const requestDirections = () => {
    if (!googleApiKey) {
      console.warn('WaypointMap: Map directions need a Google Maps API key');
      return;
    }
    if (liveNavDestination) {
      const origin = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
      const dest = liveNavDestination;
      if (!origin || !dest || !isValidMapPosition(origin) || !isValidMapPosition(dest)) return;
      liveRouteMetaRef.current = {
        lastFetchAt: Date.now(),
        lastOrigin: { lat: origin.lat, lng: origin.lng },
      };
      setRequest({
        origin,
        destination: dest,
        travelMode: 'DRIVING',
      });
      setShowDirectionsService(true);
      return;
    }
    handleRequestDirections(setRequest, setShowDirectionsService);
  };

  // Check if any loaded trips are wheelchair
  const hasWheelchairTrip = allWaypoints.some((wp) => wp.wheelChair);

  /** Never mount GoogleMap before the script exposes Map — prevents Maps v3 minified "pH" / undefined crashes. */
  const mapsRuntimeReady =
    typeof window !== 'undefined' &&
    typeof window.google?.maps?.Map === 'function' &&
    !!window.google?.maps?.event &&
    !!window.google?.maps?.SymbolPath;

  /** One frame after script+API report ready avoids a rare race with Strict Mode / overlay init. */
  const [deferredMountOk, setDeferredMountOk] = useState(false);
  useEffect(() => {
    if (!googleApiKey || !googleMapsLoaded || !mapsRuntimeReady) {
      setDeferredMountOk(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      if (
        typeof window !== 'undefined' &&
        typeof window.google?.maps?.Map === 'function' &&
        window.google?.maps?.event
      ) {
        setDeferredMountOk(true);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [googleApiKey, googleMapsLoaded, mapsRuntimeReady]);

  const canMountGoogleMap =
    !!googleApiKey && googleMapsLoaded && mapsRuntimeReady && deferredMountOk;

  return (
    <>
      <div
        className="flex flex-col rounded-sm shadow-md bg-white"
        style={{
          width: '100%',
          // If phone, expand to the parent's full height; else keep a minimum
          height: !isPhone ? '100%' : 'auto',
          minHeight: 0,
          borderTop: '1px solid lightgray',
          borderRight: '1px solid lightgray',
          borderLeft: '1px solid lightgray',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        {liveNavDestination && isAccurate && (
          <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-b border-gray-200 bg-slate-50 text-xs text-gray-700">
            <span className="font-medium text-gray-600">Route:</span>
            <button
              type="button"
              className={`rounded px-2 py-0.5 ${
                routePreference === 'fastest' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300'
              }`}
              onClick={() => persistRoutePreference('fastest')}
            >
              Fastest
            </button>
            <button
              type="button"
              className={`rounded px-2 py-0.5 ${
                routePreference === 'shortest' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300'
              }`}
              onClick={() => persistRoutePreference('shortest')}
            >
              Shortest distance
            </button>
            <span className="text-gray-500 hidden sm:inline">Updates when you move ~20 m or every ~8 s</span>
          </div>
        )}
        {canMountGoogleMap ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            onLoad={onLoad}
            onUnmount={onUnmount}
            zoom={18}
            options={mapOptions}
          >
            {liveNavDestination && isAccurate && <TrafficLayer />}

            {mapProjectionReady && remainingPolylinePath.length > 0 && (
              <Polyline
                path={remainingPolylinePath}
                options={{
                  strokeColor: '#0066cc',
                  strokeOpacity: 0.85,
                  strokeWeight: 5,
                }}
              />
            )}

            {mapProjectionReady && isAccurate && isValidMapPosition(vehicleMapPosition) && (
              <CustomMarker
                key={`veh-${vehicleMapPosition.lat.toFixed(5)}-${vehicleMapPosition.lng.toFixed(5)}`}
                position={vehicleMapPosition}
                type="vehicle"
                orientation={vehicleOrientation}
                vehicleStatus={vehicleStatus}
                hasWheelchairTrip={hasWheelchairTrip}
              />
            )}

            {mapProjectionReady && waypoint && activeRouteDestination && (
              <CustomMarker
                key={`wp-${waypoint.jobNumber}-${activeRouteDestination.lat.toFixed(5)}-${activeRouteDestination.lng.toFixed(5)}`}
                position={activeRouteDestination}
                type={String(waypoint.type || 'pickup').toLowerCase()}
                text={hasManualDropOverride ? 'DST' : `#${waypoint.jobNumber}`}
                isWheelchair={waypoint.wheelChair}
              />
            )}
          </GoogleMap>
        ) : googleApiKey && (!googleMapsLoaded || !mapsRuntimeReady || !deferredMountOk) ? (
          <div
            className="flex flex-col items-center justify-center bg-slate-200 text-slate-600 text-center px-3"
            style={{ ...mapContainerStyle, flexShrink: 0 }}
          >
            <span className="text-sm font-medium">Loading map…</span>
            <span className="text-xs mt-1">Waiting for Google Maps JavaScript API.</span>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center bg-slate-200 text-slate-600 text-center px-3"
            style={{ ...mapContainerStyle, flexShrink: 0 }}
          >
            <span className="text-sm font-medium">Map unavailable</span>
            <span className="text-xs mt-1">
              Add <code className="bg-slate-300 px-1 rounded">googleApiKey</code> to fleet settings or set{' '}
              <code className="bg-slate-300 px-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code> in{' '}
              <code className="bg-slate-300 px-1 rounded">.env</code>. Trip actions below still work.
            </span>
          </div>
        )}

        {/* Scrollable region for the trip card. Map stays locked at its height; if the card
            grows (e.g. live meter + warnings + long address), this region scrolls instead. */}
        <div
          className="flex flex-col"
          style={{
            flex: !isPhone ? '1 1 auto' : '0 0 auto',
            minHeight: 0,
            overflowY: !isPhone ? 'auto' : 'visible',
          }}
        >
        <CurrentWaypoint
          waypoint={waypoint}
          isPhone={isPhone}
          expectedTime={expectedTime}
          routeDriveSummary={routeDriveSummary}
          isAccurate={isAccurate}
          requestDirections={requestDirections}
          onWaypointAction={onWaypointAction}
          softMeterState={softMeterState}
          softMeterRouteOverride={softMeterRouteOverride}
          onSetSoftMeterRouteOverride={onSetSoftMeterRouteOverride}
          onClearSoftMeterRouteOverride={onClearSoftMeterRouteOverride}
          meterRatesReady={meterRatesReady}
          canFlag={canFlag}
          fareConfirmationOpen={fareConfirmationOpen}
          center={center}
          onFlagTripModalClosed={onFlagTripModalClosed}
          onFlagTripModalOpen={onFlagTripModalOpen}
          onFlagTripModalDismiss={onFlagTripModalDismiss}
          registerPendingFlagAck={registerPendingFlagAck}
        />
        </div>
      </div>
    </>
  );
};

export default WaypointMap;
